// Native-HLS position writes are a privileged lifecycle operation (#785).
//
// IMPORTANT: A timeline observation (position_regression, position_jump,
// waiting, stalled, or a buffered-range change) is never authority to mutate
// HTMLMediaElement.currentTime. WebKit owns ordinary native-HLS recovery. The
// application may restore position only after it has independently verified
// that the server changed the HLS delivery generation and the manifest was
// intentionally reloaded. See docs/player-resilience.md, #273, and #401.

export const NATIVE_HLS_RESTORE_REGRESSION_THRESHOLD_SECONDS = 2;
export const NATIVE_HLS_RESTORE_WRITE_TOLERANCE_SECONDS = 0.5;
export const NATIVE_HLS_RESTORE_DURATION_SAFETY_MARGIN_SECONDS = 0.25;
export const NATIVE_HLS_RESTORE_SEEKABLE_SAFETY_MARGIN_SECONDS = 0.05;

export type VerifiedNativeHlsDeliveryReload = Readonly<{
  kind: "verified_delivery_reload";
  previousGenerationId: string;
  nextGenerationId: string;
  targetAbsoluteSeconds: number;
}>;

export type NativeHlsSeekableRange = Readonly<{
  startSeconds: number;
  endSeconds: number;
}>;

export type NativeHlsPositionRestoreDecision =
  | { action: "none" }
  | { action: "wait_for_seekable_target" }
  | { action: "seek"; targetSeconds: number };

export function verifiedNativeHlsDeliveryReload(input: {
  previousGenerationId: string | null | undefined;
  nextGenerationId: string | null | undefined;
  targetAbsoluteSeconds: number;
}): VerifiedNativeHlsDeliveryReload | null {
  const previousGenerationId = input.previousGenerationId?.trim() ?? "";
  const nextGenerationId = input.nextGenerationId?.trim() ?? "";
  if (
    previousGenerationId === ""
    || nextGenerationId === ""
    || previousGenerationId === nextGenerationId
    || !Number.isFinite(input.targetAbsoluteSeconds)
    || input.targetAbsoluteSeconds < 0
  ) {
    return null;
  }
  return {
    kind: "verified_delivery_reload",
    previousGenerationId,
    nextGenerationId,
    targetAbsoluteSeconds: input.targetAbsoluteSeconds,
  };
}

export function evaluateNativeHlsPositionRestore(input: {
  request: VerifiedNativeHlsDeliveryReload;
  currentAbsoluteSeconds: number;
  durationSeconds: number;
  seekableRanges: readonly NativeHlsSeekableRange[];
}): NativeHlsPositionRestoreDecision {
  const {
    request,
    currentAbsoluteSeconds,
    durationSeconds,
    seekableRanges,
  } = input;
  if (!Number.isFinite(currentAbsoluteSeconds)) {
    return { action: "none" };
  }
  const requestedTarget = request.targetAbsoluteSeconds;
  const targetSeconds = Number.isFinite(durationSeconds) && durationSeconds > 0
    ? Math.min(
      requestedTarget,
      Math.max(0, durationSeconds - NATIVE_HLS_RESTORE_DURATION_SAFETY_MARGIN_SECONDS),
    )
    : requestedTarget;
  if (
    targetSeconds - currentAbsoluteSeconds <= NATIVE_HLS_RESTORE_REGRESSION_THRESHOLD_SECONDS
    || Math.abs(currentAbsoluteSeconds - targetSeconds) <= NATIVE_HLS_RESTORE_WRITE_TOLERANCE_SECONDS
  ) {
    return { action: "none" };
  }

  const containingRange = seekableRanges.find((range) => {
    if (!Number.isFinite(range.startSeconds) || !Number.isFinite(range.endSeconds)) {
      return false;
    }
    const safeEnd = Math.max(
      range.startSeconds,
      range.endSeconds - NATIVE_HLS_RESTORE_SEEKABLE_SAFETY_MARGIN_SECONDS,
    );
    return targetSeconds >= range.startSeconds && targetSeconds <= safeEnd;
  });
  if (!containingRange) {
    return { action: "wait_for_seekable_target" };
  }
  return { action: "seek", targetSeconds };
}
